/**
 * The acting-as banner (ADR-006 §4.3): amber, non-dismissable, on every screen while GET /me → `acting_as` is
 * set. Words, not only colour (guide §9): "Acting as {name}" or "Viewing as {name} (read-only)", the minutes
 * left, the reason, and Stop (POST /sessions/act-as/stop, then /me again).
 *
 * At `expires_at` the server has already ended it: re-read /me and say "… ended after 30 minutes". The same
 * notice covers a session ended elsewhere (another tab). Any change of identity drops cached data, which
 * belonged to the previous identity.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ROLE_LABEL, type ActingAs } from '@amplispot/contracts';
import { ACT_AS_MINUTES, refreshMe, resetOtherIdentityData, useStopActAs } from '../lib/actas';
import { useSession } from '../lib/session';
import { errorText, useToast } from '../ui/kit';
import { Icon } from '../ui/Icon';

const identity = (a: ActingAs | null) => (a ? `${a.as.id}:${a.mode}:${a.expires_at}` : '');

function useNow(intervalMs: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (intervalMs === null) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

function timeLeft(ms: number): string {
  if (ms <= 0) return 'ending now';
  const min = Math.ceil(ms / 60_000);
  return min <= 1 ? 'under a minute left' : `${min} min left`;
}

export function ActingBanner() {
  const { me } = useSession();
  const acting = me.acting_as;
  const qc = useQueryClient();
  const toast = useToast();
  const stop = useStopActAs();
  const now = useNow(acting ? 10_000 : null);
  const [ended, setEnded] = useState<string | null>(null);
  const prev = useRef<ActingAs | null>(acting);
  const stopping = useRef(false);

  // Identity changed (started, stopped, expired, ended in another tab): drop the other identity's data, and say
  // so when it ended without this tab's Stop.
  useEffect(() => {
    const was = prev.current;
    prev.current = acting;
    if (identity(was) === identity(acting)) return;
    void resetOtherIdentityData(qc);
    if (acting) {
      setEnded(null);
      return;
    }
    if (!was) return;
    if (stopping.current) {
      stopping.current = false;
      return;
    }
    const verb = was.mode === 'view' ? 'Viewing as' : 'Acting as';
    setEnded(
      Date.now() >= Date.parse(was.expires_at) - 5_000
        ? `${verb} ${was.as.name} ended after ${ACT_AS_MINUTES} minutes. You are yourself again.`
        : `${verb} ${was.as.name} was ended in another tab or window. You are yourself again.`,
    );
  }, [acting, qc]);

  // At expiry the server has already ended it: re-read /me. If it still says acting (clock drift), the 10-second
  // tick re-reads again until it does not.
  const expiresAt = acting ? Date.parse(acting.expires_at) : null;
  useEffect(() => {
    if (expiresAt === null) return;
    const ms = Math.min(Math.max(expiresAt - Date.now() + 500, 0), 2 ** 31 - 1);
    const t = window.setTimeout(() => void refreshMe(qc), ms);
    return () => window.clearTimeout(t);
  }, [expiresAt, qc]);
  useEffect(() => {
    if (expiresAt !== null && now > expiresAt + 2_000) void refreshMe(qc);
  }, [now, expiresAt, qc]);

  // The banner is a grid row of <body> (the prototype's layout); the shell's height accounts for it.
  const shown = !!acting || !!ended;
  useEffect(() => {
    document.body.classList.toggle('acting', shown);
    return () => document.body.classList.remove('acting');
  }, [shown]);

  if (!acting) {
    if (!ended) return null;
    return (
      <div className="actbar ended" role="status" aria-live="polite">
        <Icon name="swap" size={16} />
        <span className="ab-t">{ended}</span>
        <span className="grow" />
        <button className="btn sm" onClick={() => setEnded(null)}>
          OK
        </button>
      </div>
    );
  }

  const viewing = acting.mode === 'view';
  const onStop = () => {
    stopping.current = true;
    stop.mutate(undefined, {
      onSuccess: () => toast(`You are yourself again. ${viewing ? 'Viewing' : 'Acting'} as ${acting.as.name} has ended.`),
      onError: (e) => {
        stopping.current = false;
        toast(errorText(e));
      },
    });
  };

  return (
    <div className={`actbar ${viewing ? 'view' : 'act'}`} role="region" aria-label={viewing ? 'Viewing as someone else' : 'Acting as someone else'}>
      <Icon name={viewing ? 'eye' : 'swap'} size={18} />
      <div className="ab-t" aria-live="polite">
        <b>
          {viewing ? 'Viewing as' : 'Acting as'} {acting.as.name}
          {viewing ? ' (read-only)' : ''}
        </b>
        <span className="ab-m">{ROLE_LABEL[acting.as.role]}</span>
        <span className="ab-m">{viewing ? 'Nothing can be changed' : `Changes are recorded as ${acting.by.name}, acting as ${acting.as.name}`}</span>
        <span className="ab-m ab-why" title={acting.reason}>
          Why: {acting.reason}
        </span>
      </div>
      <span className="ab-left" title={`Ends at ${new Date(acting.expires_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}>
        {timeLeft((expiresAt ?? now) - now)}
      </span>
      <button className="btn sm ab-stop" onClick={onStop} disabled={stop.isPending} aria-busy={stop.isPending}>
        {stop.isPending ? 'Stopping…' : viewing ? 'Stop viewing' : 'Stop acting'}
      </button>
    </div>
  );
}
